import { ApiService } from "./../../../shared/services/api/api.service";

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import Swal from "sweetalert2";

import { GloButtonComponent } from "../../../shared/components/glo-button/glo-button.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { environment } from "../../../../environments/environment";
import { LogoHeaderComponent } from "../../../shared/components/logo-header/logo-header.component";

import { NotificationService } from "../../../shared/services/notifications-service";

@Component({
  selector: "customer-sign-up",
  standalone: true,
  imports: [
    GloButtonComponent,
    FormsModule,
    CommonModule,
    RouterModule,
    LoaderComponent,
    LogoHeaderComponent,
  ],
  templateUrl: "./sign-up.component.html",
  styleUrls: ["./sign-up.component.scss"],
})
export class CustomerSignUpComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private notification = inject(NotificationService);

  @Input() SignUpFromModal = false;
  @Input() initialEmail = "";

  @Output() handleRegisterUser = new EventEmitter<void>();
  @Output() accountCreated = new EventEmitter<string>();
  @Output() loginRequested = new EventEmitter<string>();
  @Output() otpRequested = new EventEmitter<string>();

  isLoading = false;

  assetUrl = environment.assetBaseUrl;

  full_name = "";
  email = "";
  password = "";
  role_name = "customer";

  invitation_token = "";
  isInvitedRegistration = false;

  passwordVisible = false;

  constructor(private ApiService: ApiService) {}

  ngOnInit(): void {
    // Modal registration always remains customer registration.
    if (this.SignUpFromModal) {
      this.role_name = "customer";
      return;
    }

    // Read beta invitation parameters from the URL.
    const params = this.route.snapshot.queryParamMap;

    const token = params.get("invitation_token")?.trim() || "";
    const invitedEmail = params.get("email")?.trim() || "";
    const invitedRole = params.get("role")?.trim().toLowerCase() || "";

    if (token) {
      this.isInvitedRegistration = true;
      this.invitation_token = token;

      if (invitedEmail) {
        this.email = invitedEmail;
      }

      // Only allow supported public registration roles.
      if (
        invitedRole === "business_owner" ||
        invitedRole === "customer"
      ) {
        this.role_name = invitedRole;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialEmail"] && this.initialEmail && !this.email) {
      this.email = this.initialEmail;
    }
  }

  onSubmit(): void {
    const fullName = this.full_name.trim();
    const email = this.email.trim();

    if (!fullName || !email || !this.password) {
      return;
    }

    // Preserve customer registration for booking modals.
    const registrationRole = this.SignUpFromModal
      ? "customer"
      : this.role_name;

    const payload = {
      full_name: fullName,
      email,
      password: this.password,
      role_name: registrationRole,
      invitation_token: this.SignUpFromModal
        ? ""
        : this.invitation_token,
    };

    this.isLoading = true;

    this.ApiService.request(
      API_ENDPOINTS.auth.register,
      "POST",
      payload,
      "Registration successful! You can now log in.",
      undefined,
      {
        skipAuth: true,
        showErrorToast: false,
      },
    ).subscribe({
      next: (_res: any) => {
        this.isLoading = false;

        this.notification.success(
          "Account created",
          registrationRole === "business_owner"
            ? "Your business account is ready. Sign in to continue."
            : "Your account is ready. Sign in to continue.",
        );

        if (this.SignUpFromModal) {
          this.requestOtpAfterRegistration(email, this.password);
          return;
        }

        this.router.navigateByUrl("/login");
      },

      error: (err) => {
        this.isLoading = false;

        console.error("Registration failed:", err);

        const errorMessage = this.extractRegistrationErrors(err);

        this.notification.error(
          "Unable to create account",
          errorMessage,
        );
      },
    });
  }

  private extractRegistrationErrors(error: any): string {
    const fallback = "Please check your details and try again.";

    const response = error?.error ?? error;

    if (!response) {
      return fallback;
    }

    if (typeof response === "string") {
      return response.trim() || fallback;
    }

    const messages: string[] = [];

    const fieldNames: Record<string, string> = {
      email: "Email",
      password: "Password",
      full_name: "Full name",
      phone_number: "Phone number",
      role_name: "Account type",
      invitation_token: "Invitation",
      business: "Business",
    };

    const extract = (value: any): string[] => {
      if (value == null) {
        return [];
      }

      if (typeof value === "string") {
        return [value];
      }

      if (Array.isArray(value)) {
        return value.flatMap(extract);
      }

      if (typeof value === "object") {
        return Object.values(value).flatMap(extract);
      }

      return [String(value)];
    };

    for (const [field, value] of Object.entries(response)) {
      const fieldMessages = extract(value);

      const label = fieldNames[field];

      for (const message of fieldMessages) {
        messages.push(
          label ? `${label}: ${message}` : message,
        );
      }
    }

    return messages.join("\n") || fallback;
  }

  private requestOtpAfterRegistration(
    email: string,
    password: string,
  ): void {
    this.isLoading = true;

    this.ApiService.request(
      API_ENDPOINTS.auth.requestOtp,
      "POST",
      { email, password },
      "Verification code sent to your email!",
      undefined,
      { skipAuth: true },
    ).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.accountCreated.emit(email);
        this.otpRequested.emit(response?.email || email);
      },

      error: (err) => {
        this.isLoading = false;

        console.error(
          "Failed to start verification after registration:",
          err,
        );

        this.accountCreated.emit(email);

        Swal.fire({
          title: "Account created",
          text:
            "Your account was created, but we could not send the verification code. Please sign in to retry.",
          icon: "warning",
          confirmButtonText: "Continue",
        });
      },
    });
  }

  requestLogin(): void {
    if (this.SignUpFromModal) {
      this.loginRequested.emit(this.email.trim());
      return;
    }

    this.handleRegisterUser.emit();
  }

  toggleVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }
}